/** Formatação pt-BR. Centralizada para o app inteiro falar a mesma língua. */

const brl = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
});
const brlExato = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const num = new Intl.NumberFormat('pt-BR');

/** R$ 1.234 — sem centavos, para KPI. Centavos em título viram ruído. */
export const moeda = (v: number | string | null | undefined): string =>
  brl.format(Number(v ?? 0));

/** R$ 1.234,56 — com centavos, para valores que alguém vai conferir. */
export const moedaExata = (v: number | string | null | undefined): string =>
  brlExato.format(Number(v ?? 0));

export const numero = (v: number | string | null | undefined): string => num.format(Number(v ?? 0));

/**
 * Moeda abreviada, para caber em cartão de celular.
 *
 * `R$ 1.500.000,00` tem 16 caracteres e estoura a largura de um cartão em
 * 360px; `R$ 1,5 mi` tem 9. Abrevia só a partir de mil — abaixo disso o valor
 * cheio cabe e é mais preciso.
 *
 * As abreviações são as do português (mil, mi, bi), não as inglesas (K, M, B),
 * que o usuário desta interface não usa.
 */
export function moedaCompacta(v: number | string | null | undefined): string {
  const n = Number(v ?? 0);
  const abs = Math.abs(n);

  if (abs >= 1_000_000_000) return `R$ ${arredondar(n / 1_000_000_000)} bi`;
  if (abs >= 1_000_000) return `R$ ${arredondar(n / 1_000_000)} mi`;
  if (abs >= 10_000) return `R$ ${arredondar(n / 1_000)} mil`;
  return brl.format(n);
}

/** Uma casa decimal só quando ela muda a leitura. */
function arredondar(n: number): string {
  const uma = Math.round(n * 10) / 10;
  return Number.isInteger(uma) ? String(uma) : uma.toFixed(1).replace('.', ',');
}

/** Contagem abreviada — mesma lógica, sem o símbolo da moeda. */
export function numeroCompacto(v: number | string | null | undefined): string {
  const n = Number(v ?? 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${arredondar(n / 1_000_000)} mi`;
  if (abs >= 100_000) return `${arredondar(n / 1_000)} mil`;
  return num.format(n);
}

export const percentual = (v: number | null | undefined, casas = 0): string =>
  `${Number(v ?? 0).toFixed(casas).replace('.', ',')}%`;

/** "11 ago" — eixo de gráfico e listas, onde o ano é redundante. */
export function diaMes(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
}

/** "agosto de 2026" a partir de "2026-08". */
export function competencia(anoMes: string): string {
  const [ano, mes] = anoMes.split('-');
  const d = new Date(Number(ano), Number(mes) - 1, 1);
  return d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

/** Competência atual no fuso de Cuiabá, no formato YYYY-MM. */
export function competenciaAtual(): string {
  const agora = new Date();
  const local = new Date(agora.toLocaleString('en-US', { timeZone: 'America/Cuiaba' }));
  return `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}`;
}

const PARTICULAS = new Set(['da', 'das', 'de', 'do', 'dos', 'e']);

/**
 * "ROGERIO DIEGO DA SILVA" → "Rogerio Diego da Silva". O SGA grava nomes em
 * caixa alta, que numa lista inteira lê como se fosse grito.
 */
export function nomeProprio(nome: string): string {
  return nome
    .toLocaleLowerCase('pt-BR')
    .split(/\s+/)
    .map((p, i) => (i > 0 && PARTICULAS.has(p) ? p : p.charAt(0).toLocaleUpperCase('pt-BR') + p.slice(1)))
    .join(' ');
}

/** Primeiro nome — cabeçalhos no mobile não comportam o nome inteiro. */
export const primeiroNome = (nome: string): string => nome.trim().split(/\s+/)[0] ?? nome;

/** 000.000.000-00 — devolve vazio se não houver CPF. */
export function formatarCpf(cpf: string | null | undefined): string {
  const d = (cpf ?? '').replace(/\D/g, '');
  if (d.length !== 11) return d;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** (00) 00000-0000 — o espelho guarda só dígitos, com DDD. */
export function formatarTelefone(tel: string | null | undefined): string {
  const d = (tel ?? '').replace(/\D/g, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return d;
}

/** 00000-000 — devolve o que vier se não tiver 8 dígitos. */
export function formatarCep(cep: string | null | undefined): string {
  const d = (cep ?? '').replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

export const iniciais = (nome: string): string =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
